// store.jsx — CardStatusContext (lógica de borrado y permisos)
import { createContext } from "preact";
import { useContext, useMemo, useState, useCallback } from "preact/hooks";
import { fetchToServer } from "@shared/components";
import { useQuotationByIdMultiRole } from "../store"; // 👈 Contexto padre
import { navigate } from "../../../utils/Navigator"; // Ajusta la ruta si es necesario, o usa window.location

export const CardStatusContext = createContext(null);

export function useCardStatus() {
  const ctx = useContext(CardStatusContext);
  if (!ctx) throw new Error("useCardStatus must be used within CardStatusProvider");
  return ctx;
}

export function CardStatusProvider({ children }) {
  const { quotation, quotationId, role, refresh } = useQuotationByIdMultiRole();

  const [deleting, setDeleting] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [error, setError] = useState(null);

  const statusName = String(quotation?.status?.name || "").toLowerCase();

  // Validaciones de permisos
  const canDelete = useMemo(() => {
    const allowed = ["created", "ai-review", "client-validation", "rejected", "client-rejected"];
    return allowed.includes(statusName);
  }, [statusName]);

  // Acción de borrado
  const deleteQuotation = useCallback(async () => {
    if (!quotationId) return;
    setDeleting(true);
    setError(null);
    try {
      await fetchToServer({
        url: `/${role}/api/quotations/${quotationId}`,
        method: "delete",
        withCredentials: true,
      });
      // Redirigir tras borrar
      navigate(`/${role}/quotations`); 
    } catch (e) {
      setError(e);
      setDeleting(false); // Solo bajamos flag si falló, si tuvo éxito navegamos fuera
      throw e;
    }
  }, [role, quotationId]);

  const value = useMemo(
    () => ({
      quotation,
      role,
      statusName,
      canDelete,
      deleting,
      error,
      openDelete,
      setOpenDelete,
      deleteQuotation,
    }),
    [quotation, role, statusName, canDelete, deleting, error, openDelete, setOpenDelete, deleteQuotation]
  );

  return (
    <CardStatusContext.Provider value={value}>
      {children}
    </CardStatusContext.Provider>
  );
}
